import type { FunctionComponent } from 'react'
import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import publicConfig from '@/config/public'
import { buildAssetsPath } from '@/libs/helpers/assets'
import { DefaultThemeProvider, Grid, SvgIcon, Text } from '@/tbui'
import { textStyles } from '../../tbui/Text/styles'

const StyledRow = styled(Grid)<{ isECardEnabled?: boolean }>`
  gap: 0.5rem;
  @media (max-width: ${(props) => props.theme.breakpoints.SM}) {
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;
  }
  border-radius: 12px;
  height: 90px;
  display: flex;

  ${(props) =>
    props.isECardEnabled
      ? `
    background-color: transparent;
    border-color: ${props.theme.colors.FEATHER_GREY};
    border-width: 1px;
    border-style: solid;
    `
      : `
    background-color: ${props.theme.palette.SECONDARY};
      `}

  ${(props) =>
    props.isECardEnabled
      ? `@container (width < 350px) {
    height: 120px;
  }`
      : ''}
`

const StyledContentRow = styled(Grid)`
  display: flex;
  justify-content: space-between;

  @container (max-width: ${(props) => props.theme.breakpoints.XS}) {
    margin-left: 9px;
  }
`

const StyledTitleText = styled(Text)`
  color: ${({ theme }) => theme.palette.PRIMARY};
`

const StyledDescriptionText = styled.p`
  color: ${({ theme }) => theme.palette.PRIMARY};
  ${({ theme }) => textStyles(theme).base.p}
  margin: 0.5rem 0 0 0;
  display: block;
`

const StyledSvg = styled.svg<{ isOrderPage?: boolean }>`
  fill: none;
  height: 90px;
  @container (width >= 550px) {
    width: 350px;
  }

  .content {
    transform: translate(-130px, 0);
  }

  .card {
    transform: translate(-20px, 0);
  }

  @container (width < 550px) {
    min-width: 125px;
    width: 150px;
    .circle {
      transform: scale(0.35, 0.35) translate(260px, 120px);
    }

    .card {
      transform: translate(70px, 65px) rotate(-15deg) scale(0.4, 0.4);
    }
  }

  @container (width < 350px) {
    height: 120px;
    min-width: 125px;
    width: 150px;
    .circle {
      transform: scale(0.35, 0.35) translate(260px, 180px);
    }

    .content {
      transform: translate(-120px, 0);
    }

    .card {
      transform: translate(65px, 95px) rotate(-15deg) scale(0.4, 0.4);
    }
  }
`

const StyledImg = styled.img<{ isOrderPage?: boolean }>`
  border-bottom-right-radius: 28px;
  border-top-right-radius: 28px;
  height: 90px;

  ${(props) =>
    !props.isOrderPage
      ? `@media (min-width: ${props.theme.breakpoints.MD}) {
    content: url(${buildAssetsPath('/images/tb-gift-card-mockup.png')});
    border-bottom-left-radius: 3% 98%;
    border-top-left-radius: 3% 98%;
  }
  @media (
    max-width: ${props.theme.breakpoints.MD}) {
    content: url(${buildAssetsPath('/images/tb-gift-card-mockup-small.png')});
    border-bottom-left-radius: 49% 104%;
    border-top-left-radius: 56% 127%;
  }

  @media (max-width: ${props.theme.breakpoints.LG}) and (min-width: ${props.theme.breakpoints.MD}) {
    width: 60%;
  }`
      : `content: url(${buildAssetsPath('/images/tb-gift-card-mockup-small.png')});
      border-bottom-left-radius: 49% 104%;
      border-top-left-radius: 56% 127%;
  `};
`

const ArrowSvg = styled(SvgIcon)`
  display: inline-flex;
  vertical-align: middle;
  margin-left: 0.5rem;
`

const StyledAnchor = styled.a`
  text-decoration: none;
  display: block;
  margin-top: 10px;
  container-type: inline-size;
`

interface DGCBannerProps {
  venueName: string
  loyaltyRestaurantGroupID: number
  isSignedIn: boolean
  isOrderPage?: boolean
  isECardsEnabled: boolean
  giftCardBannerUrl?: string
}

const bannerSVG = (theme: Theme): React.ReactElement => {
  return (
    <StyledSvg>
      <mask id="mask" mask-type="luminance" maskUnits="userSpaceOnUse">
        <rect x="0" y="0" width="100%" height="100%" rx="8" ry="8" fill="white" />
      </mask>
      <g mask="url(#mask)">
        <g className="content">
          <circle
            className="circle"
            cx="382.979"
            cy="41.9769"
            r="250.705"
            fill={theme.palette.PRIMARY}
          />
          <g className="card">
            <rect
              x="242.199"
              y="36.1677"
              width="214.933"
              height="149.631"
              rx="8"
              ry="8"
              transform="rotate(-30 242.199 36.1677)"
              fill={theme.palette.SECONDARY}
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M401.681 30.472C410.856 46.3642 405.411 66.6865 389.519 75.8619C373.628 85.0363 353.306 79.591 344.13 63.6988C334.955 47.8065 340.401 27.4858 356.292 18.3114C372.184 9.13599 392.505 14.5797 401.681 30.472Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M383.75 30.5178L378.814 33.3674C379.073 32.1501 378.92 30.8404 378.251 29.6803C376.813 27.1911 373.62 26.3353 371.13 27.7724C367.317 29.9739 367.243 34.4777 367.678 37.4292C365.339 35.5772 361.401 33.3894 357.588 35.5909C355.099 37.028 354.243 40.2219 355.681 42.7111C356.35 43.8712 357.408 44.6583 358.592 45.0426L353.656 47.8922C353.24 48.1323 353.098 48.6631 353.338 49.0789L357.682 56.6022C357.922 57.0181 358.453 57.1603 358.869 56.9202L361.126 55.6171L369.378 69.9115C369.619 70.3273 370.149 70.4695 370.565 70.2295L394.64 56.3299C395.056 56.0898 395.198 55.5591 394.958 55.1432L386.705 40.8489L388.962 39.5458C389.378 39.3057 389.52 38.775 389.28 38.3591L384.936 30.8358C384.696 30.4199 384.165 30.2777 383.75 30.5178ZM371.999 29.2771C373.658 28.3193 375.788 28.8893 376.746 30.549C377.704 32.2087 377.133 34.338 375.474 35.2958L369.679 38.6417C369.167 36.4383 368.434 31.3352 371.999 29.2771ZM358.457 37.0956C356.798 38.0534 356.227 40.1827 357.185 41.8423C358.143 43.502 360.273 44.072 361.932 43.1142L367.727 39.7683C366.075 38.2232 362.022 35.0374 358.457 37.0956ZM355.277 48.9625L362.801 44.6189L368.819 41.144L372.294 47.1627L358.752 54.9812L355.277 48.9625ZM373.163 48.6674L362.63 54.7484L370.449 68.2904L380.981 62.2094L373.163 48.6674ZM393.019 55.2596L382.486 61.3407L374.668 47.7986L385.2 41.7176L393.019 55.2596ZM373.799 46.294L387.341 38.4755L383.866 32.4568L376.343 36.8004L370.324 40.2753L373.799 46.294Z"
              fill={theme.palette.SECONDARY}
            />
          </g>
        </g>
      </g>
    </StyledSvg>
  )
}

const DGCBanner: FunctionComponent<DGCBannerProps> = ({
  venueName,
  loyaltyRestaurantGroupID,
  isSignedIn,
  isOrderPage,
  isECardsEnabled,
  giftCardBannerUrl,
}) => {
  // HOOKS
  const { t } = useTranslation()
  const { cwaURL } = publicConfig
  const theme = useTheme()
  let href = isSignedIn
    ? `${cwaURL}/${loyaltyRestaurantGroupID}/home/giftcards/create-card?autoLogin=true`
    : `${cwaURL}/${loyaltyRestaurantGroupID}/home/giftcards`
  if (isECardsEnabled && giftCardBannerUrl) {
    href = giftCardBannerUrl
  }
  const banner = (
    <>
      <StyledAnchor target="_blank" href={href} data-test="dgc-banner">
        <StyledRow align="center" isECardEnabled={isECardsEnabled}>
          <StyledContentRow gap="8px" m="0 0 0 36px" align="center">
            <div>
              <StyledTitleText type="H3" data-test="dgc-banner-title">
                {t('venue.summary.banner.title')}
              </StyledTitleText>
              <StyledDescriptionText data-test="dgc-banner-subtitle">
                {t('venue.summary.banner.subtitle', { restaurant: venueName })}
                <ArrowSvg name="arrow" color="PRIMARY" fontSize="15px" />
              </StyledDescriptionText>
            </div>
            {isECardsEnabled ? (
              bannerSVG(theme)
            ) : (
              <StyledImg isOrderPage={isOrderPage} alt="tb-gift-card-mockup" />
            )}
          </StyledContentRow>
        </StyledRow>
      </StyledAnchor>
    </>
  )
  return isECardsEnabled ? <>{banner}</> : <DefaultThemeProvider>{banner}</DefaultThemeProvider>
}

export default DGCBanner
